// tinker: intervalTour — meet the distances above home, each with its flavor
// word from INTERVAL_FLAVORS. Two modes via config.mode:
//   melodic  → tap a distance chip to hear home, then the note that distance
//              above it — the interval as a move.
//   harmonic → tap a chip to hear both notes at once — the interval as a
//              color.
// The piano stays live underneath (pointer + typing + MIDI) so learners can
// hunt for the same distances by hand. Always ready to continue; a "begin"
// tap resumes audio and fades the drone in, matching the other tinkers.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { HOME_MIDI, INTERVAL_FLAVORS } from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playNote,
  startDrone,
  resumeAudio,
} from "../../../lib/homeKeyAudio";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

// Sound the pairs an octave above the G3 home so they sit clear of the drone
// bed; the octave chip tops out exactly at G5, the piano's highest key.
const BASE_MIDI = HOME_MIDI + 12;
const SUSTAIN_MS = 1600;
const GAP_MS = 500; // melodic mode: the far note follows home by this much

export default function TinkerIntervalTour({ moment, onReadyChange }) {
  const config = moment.config || {};
  const mode = config.mode || "melodic";

  const [begun, setBegun] = useState(false);
  const [picked, setPicked] = useState(null); // the interval object last tapped
  const [ringing, setRinging] = useState(null); // Set of midis lit while it rings
  const droneRef = useRef(null);
  const ringTimer = useRef(null);

  const voices = useHomeKeyVoices(PATCHES.softKeys);
  const { press, release, activeMidis } = voices;

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (droneRef.current) droneRef.current.stop();
    droneRef.current = null;
    if (ringTimer.current) clearTimeout(ringTimer.current);
    ringTimer.current = null;
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  useTypingKeys(press, release, begun);
  useHomeKeyMidi(press, release, begun);

  const begin = () => {
    resumeAudio();
    if (!droneRef.current) droneRef.current = startDrone();
    setBegun(true);
  };

  const tap = (interval) => {
    resumeAudio();
    const low = BASE_MIDI;
    const high = BASE_MIDI + interval.semis;
    let totalMs = SUSTAIN_MS;
    if (mode === "harmonic") {
      playNote(PATCHES.softKeys, low, SUSTAIN_MS);
      playNote(PATCHES.softKeys, high, SUSTAIN_MS);
    } else {
      playNote(PATCHES.softKeys, low, 450);
      playNote(PATCHES.softKeys, high, SUSTAIN_MS, GAP_MS);
      totalMs = GAP_MS + SUSTAIN_MS;
    }
    setPicked(interval);
    setRinging(new Set([low, high]));
    if (ringTimer.current) clearTimeout(ringTimer.current);
    ringTimer.current = setTimeout(() => setRinging(null), totalMs);
  };

  const lit = ringing ? new Set([...activeMidis, ...ringing]) : activeMidis;

  const defaultPrompt =
    mode === "harmonic"
      ? "Now hear each distance as two notes at once — the move becomes a color. Tap a distance and let it hang in the air."
      : "Every distance from home has a flavor. Tap one to hear home step out to it — then hunt for the same gap on the keys yourself.";

  return (
    <div className="hk-moment-body">
      <p>{config.prompt || defaultPrompt}</p>

      {!begun && (
        <button type="button" className="hk-btn hk-btn--primary" onClick={begin}>
          begin — fade the drone in
        </button>
      )}

      {begun && (
        <>
          <div className="hk-choice-row" role="group" aria-label="distances above home">
            {INTERVAL_FLAVORS.map((interval) => (
              <button
                key={interval.steps}
                type="button"
                className={`hk-choice${
                  picked && picked.steps === interval.steps ? " hk-choice--picked" : ""
                }`}
                aria-pressed={picked ? picked.steps === interval.steps : false}
                onClick={() => tap(interval)}
              >
                {interval.label}
              </button>
            ))}
          </div>
          {picked != null && (
            <p className="hk-persona" aria-live="polite">
              <strong>{picked.label}</strong> — {picked.flavor}
            </p>
          )}
        </>
      )}

      <Piano
        activeMidis={lit}
        onPress={press}
        onRelease={release}
        highlight={{ scaleOnly: true }}
        disabled={!begun}
      />
    </div>
  );
}
